import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdArrowBack, MdPhotoCamera } from 'react-icons/md';
import ToastContainer, { useToast } from '../../components/ToastContainer';
import { getUser, getAuthToken, saveAuth } from '../../services/auth';
import { updateUserProfile } from '../../services/api';
import { t } from '../../utils/i18n';

const defaultAddress = {
  street: '',
  city: '',
  postalCode: '',
};

export default function EditProfilePage() {
  const navigate = useNavigate();
  const { showToast, toasts, removeToast } = useToast();
  const [user, setUser] = useState(() => getUser());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [address, setAddress] = useState(defaultAddress);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    hourlyRate: '',
  });
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/dashboard/profile', { replace: true });
      return;
    }

    setForm({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      phone: user.phone || '',
      hourlyRate: user.hourlyRate ? String(user.hourlyRate) : '',
    });

    if (user.address) {
      setAddress({
        street: user.address.street || '',
        city: user.address.city || '',
        postalCode: user.address.postalCode || user.address.zip || '',
      });
    }
    if (user?.photoUrl || user?.profileImage) {
      setPhotoPreview(user.photoUrl || user.profileImage);
    } else {
      setPhotoPreview(null);
    }
  }, [user, navigate]);

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        showToast(t('profile.photoTooLarge') || 'Fotografija je prevelika. Maksimalna veličina je 5MB.', 'error');
        return;
      }
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleInputChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleAddressChange = (key, value) => {
    setAddress((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setIsSubmitting(true);
    try {
      const payload = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.trim(),
      };

      if (user.role === 'babysitter' && form.hourlyRate) {
        payload.hourlyRate = Number(form.hourlyRate);
      }

      const cleanAddress = {
        street: address.street.trim(),
        city: address.city.trim(),
        postalCode: address.postalCode.trim(),
      };

      if (cleanAddress.street || cleanAddress.city || cleanAddress.postalCode) {
        payload.address = cleanAddress;
      }

      // Handle photo upload - compress and convert to base64
      if (photoFile) {
        try {
          const reader = new FileReader();
          reader.onloadend = async () => {
            try {
              // Compress image before sending
              const img = new Image();
              img.onload = async () => {
                try {
                  const canvas = document.createElement('canvas');
                  const maxWidth = 800;
                  const maxHeight = 800;
                  let width = img.width;
                  let height = img.height;

                  // Calculate new dimensions
                  if (width > height) {
                    if (width > maxWidth) {
                      height = (height * maxWidth) / width;
                      width = maxWidth;
                    }
                  } else {
                    if (height > maxHeight) {
                      width = (width * maxHeight) / height;
                      height = maxHeight;
                    }
                  }

                  canvas.width = width;
                  canvas.height = height;
                  const ctx = canvas.getContext('2d');
                  ctx.drawImage(img, 0, 0, width, height);

                  // Convert to base64 with compression (0.7 quality)
                  let compressedBase64 = canvas.toDataURL('image/jpeg', 0.7);
                  
                  // Check size (limit to ~300KB base64 = ~225KB actual)
                  if (compressedBase64.length > 300000) {
                    // Try with lower quality
                    compressedBase64 = canvas.toDataURL('image/jpeg', 0.5);
                  }
                  
                  // If still too large, reduce dimensions further
                  if (compressedBase64.length > 300000) {
                    const smallerCanvas = document.createElement('canvas');
                    smallerCanvas.width = Math.min(width, 600);
                    smallerCanvas.height = Math.min(height, 600);
                    const smallerCtx = smallerCanvas.getContext('2d');
                    smallerCtx.drawImage(img, 0, 0, smallerCanvas.width, smallerCanvas.height);
                    compressedBase64 = smallerCanvas.toDataURL('image/jpeg', 0.5);
                  }
                  
                  // Add profileImage to payload
                  const payloadWithImage = {
                    ...payload,
                    profileImage: compressedBase64
                  };
                  
                  const response = await updateUserProfile(payloadWithImage);
                  if (response?.user) {
                    const token = getAuthToken();
                    if (token) {
                      saveAuth(token, response.user);
                    }
                    setUser(response.user);
                    // Update photo preview with the saved URL if available
                    if (response.user.photoUrl || response.user.profileImage) {
                      setPhotoPreview(response.user.photoUrl || response.user.profileImage);
                    }
                    showToast(t('profile.updateSuccess') || 'Profil je uspešno ažuriran', 'success', 4000);
                    setTimeout(() => {
                      navigate('/dashboard/profile', { replace: true });
                    }, 500);
                  } else {
                    throw new Error('Invalid response');
                  }
                } catch (error) {
                  console.error('Profile update error:', error);
                  const message = error.response?.data?.message || t('profile.updateError') || 'Greška pri ažuriranju profila';
                  showToast(message, 'error', 5000);
                } finally {
                  setIsSubmitting(false);
                }
              };
              img.onerror = () => {
                showToast(t('profile.photoError') || 'Greška pri učitavanju slike', 'error');
                setIsSubmitting(false);
              };
              img.src = reader.result;
            } catch (error) {
              console.error('Image processing error:', error);
              showToast(t('profile.photoError') || 'Greška pri obradi slike', 'error');
              setIsSubmitting(false);
            }
          };
          reader.readAsDataURL(photoFile);
          return; // Exit early, will continue in reader.onloadend
        } catch (error) {
          console.error('File reading error:', error);
          showToast(t('profile.photoError') || 'Greška pri čitanju fajla', 'error');
          setIsSubmitting(false);
        }
      }

      const response = await updateUserProfile(payload);
      if (response?.user) {
        const token = getAuthToken();
        if (token) {
          saveAuth(token, response.user);
        }
        setUser(response.user);
        showToast(t('profile.updateSuccess') || 'Profile updated successfully', 'success', 4000);
        setTimeout(() => {
          navigate('/dashboard/profile', { replace: true });
        }, 500);
      } else {
        throw new Error('Invalid response');
      }
    } catch (error) {
      console.error('Profile update error:', error);
      const message = error.response?.data?.message || t('profile.updateError') || 'Error updating profile';
      showToast(message, 'error', 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden relative">
      <div className="relative z-10 p-4 lg:p-6">
        <div className="max-w-2xl mx-auto">
          <button
            onClick={() => navigate('/dashboard/profile')}
            className="mb-6 flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
          >
            <MdArrowBack className="text-xl" />
            <span>{t('common.back') || 'Back'}</span>
          </button>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 lg:p-6">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">
              {t('profile.editProfile') || 'Edit Profile'}
            </h1>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Photo Upload */}
              <div className="flex flex-col items-center mb-6">
                <div className="relative">
                  <div className="w-32 h-32 rounded-full bg-primary-600 flex items-center justify-center text-white text-4xl font-bold overflow-hidden">
                    {photoPreview ? (
                      <img src={photoPreview} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      user?.firstName?.[0]?.toUpperCase() || 'U'
                    )}
                  </div>
                  <label className="absolute bottom-0 right-0 bg-primary-600 text-white p-2 rounded-full cursor-pointer hover:bg-primary-700 transition-colors">
                    <MdPhotoCamera className="text-xl" />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      className="hidden"
                    />
                  </label>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  {photoPreview ? t('profile.changePhoto') : t('profile.uploadPhoto')}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('profile.firstName') || 'First Name'}
                  </label>
                  <input
                    type="text"
                    value={form.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('profile.lastName') || 'Last Name'}
                  </label>
                  <input
                    type="text"
                    value={form.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('profile.phone') || 'Phone'}
                </label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  required
                />
              </div>

              {user?.role === 'babysitter' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('profile.hourlyRate') || 'Hourly Rate (RSD/h)'}
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={form.hourlyRate}
                    onChange={(e) => handleInputChange('hourlyRate', e.target.value)}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder="800"
                  />
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('profile.street') || 'Street'}
                  </label>
                  <input
                    type="text"
                    value={address.street}
                    onChange={(e) => handleAddressChange('street', e.target.value)}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('profile.city') || 'City'}
                  </label>
                  <input
                    type="text"
                    value={address.city}
                    onChange={(e) => handleAddressChange('city', e.target.value)}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('profile.postalCode') || 'Postal Code'}
                  </label>
                  <input
                    type="text"
                    value={address.postalCode}
                    onChange={(e) => handleAddressChange('postalCode', e.target.value)}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (t('common.saving') || 'Saving...') : (t('common.save') || 'Save')}
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/dashboard/profile')}
                  className="btn-secondary"
                >
                  {t('common.cancel') || 'Cancel'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
}

